import type { Assistant } from "@inrev/assistant";
import { Option } from "@inrev/core";

type OrgChartEntity = Assistant.Dtos.Agent.OrgChart.Entity;

/** One ownership line: from the owner above to the entity it holds below. */
export type DiagramEdge = {
	ownerId: number;
	ownedId: number;
	/** The stake as the notes state it, drawn on the line. */
	pct: string | null;
	/** A parent's line; a minority stake is drawn lighter, since it is not control. */
	kind: "parent" | "stake";
	/** Which end of the line its label sits by: the one fewer lines share, so labels do not pile. */
	labelEnd: "owner" | "owned";
};

/** A box's top-left corner. */
export type DiagramNode = { entity: OrgChartEntity; x: number; y: number };

/**
 * A parent with more subsidiaries than a row holds, drawn as draw.io draws a long list: the
 * subsidiaries stacked in columns, each column hung from a vertical trunk.
 */
export type DiagramStack = {
	parentId: number;
	/** Where the drop from the parent meets the trunks' crossbar. */
	busY: number;
	columns: { trunkX: number; memberIds: number[] }[];
};

export type Diagram = {
	nodes: DiagramNode[];
	edges: DiagramEdge[];
	stacks: DiagramStack[];
	width: number;
	height: number;
};

export const BOX_WIDTH = 168;
export const BOX_HEIGHT = 56;
const COLUMN_GAP = 24;
const ROW_GAP = 64;
const MARGIN = 8;
const PASSES = 4;
/** A parent with more subsidiaries that hold nothing than this stacks them in columns. */
const STACK_AFTER = 5;
const STACK_ROWS = 8;
const STACK_GAP = 12;
const TRUNK_INDENT = 20;
/** A parent's line pulls a box harder than a minority stake, so a parent sits over its group. */
const PARENT_WEIGHT = 3;

/**
 * Every line in the structure. uw-agent already drops a parent or stake that names no listed
 * entity, so each end is a box; this repeats that check only so a bad link cannot crash the panel.
 */
export const diagramEdges = (entities: readonly OrgChartEntity[]): DiagramEdge[] => {
	const ids = new Set(entities.map((entity) => entity.id));
	const lines: Omit<DiagramEdge, "labelEnd">[] = [];
	for (const entity of entities) {
		if (entity.parentKind === "entity" && entity.parentId !== null && ids.has(entity.parentId)) {
			lines.push({
				ownerId: entity.parentId,
				ownedId: entity.id,
				pct: entity.ownershipPct,
				kind: "parent",
			});
		}
		// Two stakes in one entity (common and preferred) share a line, which would draw them on top of each other.
		const pctsByOwned = new Map<number, (string | null)[]>();
		for (const stake of entity.stakes) {
			if (!ids.has(stake.entityId) || stake.entityId === entity.id) continue;
			append(pctsByOwned, stake.entityId, stake.pct);
		}
		for (const [ownedId, pcts] of pctsByOwned) {
			const stated = Option.filterNone(pcts);
			lines.push({
				ownerId: entity.id,
				ownedId,
				pct: stated.length === 0 ? null : stated.join(", "),
				kind: "stake",
			});
		}
	}
	const out = tally(lines.map((line) => line.ownerId));
	const into = tally(lines.map((line) => line.ownedId));
	return lines.map((line) => ({
		...line,
		labelEnd: (out.get(line.ownerId) ?? 0) >= (into.get(line.ownedId) ?? 0) ? "owned" : "owner",
	}));
};

const tally = (ids: readonly number[]) => {
	const counts = new Map<number, number>();
	for (const id of ids) counts.set(id, (counts.get(id) ?? 0) + 1);
	return counts;
};

/** Appends to a map's list for a key, creating it, without copying the list each time. */
export const append = <K, V>(map: Map<K, V[]>, key: K, value: V) => {
	const list = map.get(key);
	if (list === undefined) map.set(key, [value]);
	else list.push(value);
};

const mean = (values: number[]): number | undefined =>
	values.length === 0
		? undefined
		: values.reduce((total, value) => total + value, 0) / values.length;

const weightedMean = (pairs: { value: number; weight: number }[]): number | undefined => {
	const weight = pairs.reduce((total, pair) => total + pair.weight, 0);
	return weight === 0
		? undefined
		: pairs.reduce((total, pair) => total + pair.value * pair.weight, 0) / weight;
};

/**
 * Parents with too many subsidiaries for one row. A subsidiary joins its parent's stack only
 * when the parent is its sole owner and it holds nothing, so no other line has to reach it.
 */
const stackGroups = (entities: readonly OrgChartEntity[], edges: readonly DiagramEdge[]) => {
	const holds = new Set(edges.map((edge) => edge.ownerId));
	const ownersOf = new Map<number, DiagramEdge[]>();
	for (const edge of edges) append(ownersOf, edge.ownedId, edge);
	const groups = new Map<number, number[]>();
	for (const entity of entities) {
		const owners = ownersOf.get(entity.id) ?? [];
		const only = owners[0];
		if (owners.length !== 1 || only === undefined || only.kind !== "parent" || holds.has(entity.id))
			continue;
		append(groups, only.ownerId, entity.id);
	}
	return new Map([...groups].filter(([, members]) => members.length > STACK_AFTER));
};

const stackColumns = (members: number[]) =>
	Array.from({ length: Math.ceil(members.length / STACK_ROWS) }, (_, column) =>
		members.slice(column * STACK_ROWS, (column + 1) * STACK_ROWS),
	);

type Slot = { width: number; height: number };
type Link = { owner: number; owned: number; weight: number; kind: DiagramEdge["kind"] };

/**
 * The links rows are ranked by: parent links first, then stakes, each kept only if it closes no
 * loop. A subsidiary's stake in its own parent would otherwise put the parent below it.
 */
const rankingLinks = (links: readonly Link[]): Link[] => {
	const below = new Map<number, number[]>();
	const reaches = (from: number, to: number) => {
		const seen = new Set<number>();
		const pending = [from];
		for (let id = pending.pop(); id !== undefined; id = pending.pop()) {
			if (id === to) return true;
			if (seen.has(id)) continue;
			seen.add(id);
			pending.push(...(below.get(id) ?? []));
		}
		return false;
	};
	const kept: Link[] = [];
	for (const link of [
		...links.filter((candidate) => candidate.kind === "parent"),
		...links.filter((candidate) => candidate.kind === "stake"),
	]) {
		if (reaches(link.owned, link.owner)) continue;
		append(below, link.owner, link.owned);
		kept.push(link);
	}
	return kept;
};

/**
 * Positions for a draw.io-style structure chart: boxes in rows, owners above what they hold,
 * each line labeled with its stake.
 */
export const orgChartDiagram = (entities: readonly OrgChartEntity[]): Diagram => {
	// Nothing to place, and Math.min of no extents would be Infinity.
	if (entities.length === 0) {
		return { nodes: [], edges: [], stacks: [], width: 2 * MARGIN, height: BOX_HEIGHT + 2 * MARGIN };
	}
	const edges = diagramEdges(entities);
	const stacks = stackGroups(entities, edges);
	const stackOf = new Map(
		[...stacks].flatMap(([parentId, members]) => members.map((id) => [id, parentId] as const)),
	);
	// A stack stands in its row as one wide slot, under an id above every entity's.
	const firstSlotId = Math.max(...entities.map((entity) => entity.id)) + 1;
	const stackSlotId = new Map(
		[...stacks.keys()].map((parentId, at) => [parentId, firstSlotId + at]),
	);
	const stackParentId = new Map([...stackSlotId].map(([parentId, slotId]) => [slotId, parentId]));
	const slotOf = (id: number) => {
		const parentId = stackOf.get(id);
		return parentId === undefined ? id : (stackSlotId.get(parentId) ?? id);
	};
	const links: Link[] = edges.map((edge) => ({
		owner: slotOf(edge.ownerId),
		owned: slotOf(edge.ownedId),
		weight: edge.kind === "parent" ? PARENT_WEIGHT : 1,
		kind: edge.kind,
	}));

	const slots = new Map<number, Slot>();
	for (const entity of entities) {
		if (!stackOf.has(entity.id)) slots.set(entity.id, { width: BOX_WIDTH, height: BOX_HEIGHT });
	}
	for (const [parentId, members] of stacks) {
		const slotId = stackSlotId.get(parentId);
		if (slotId === undefined) continue;
		const columns = stackColumns(members).length;
		const rows = Math.min(STACK_ROWS, members.length);
		slots.set(slotId, {
			width: columns * (BOX_WIDTH + TRUNK_INDENT) + (columns - 1) * COLUMN_GAP,
			height: rows * BOX_HEIGHT + (rows - 1) * STACK_GAP,
		});
	}

	// Each slot one row below its lowest owner.
	const ownersOf = new Map<number, number[]>();
	for (const link of rankingLinks(links)) append(ownersOf, link.owned, link.owner);
	const rowOf = new Map<number, number>();
	const row = (id: number): number => {
		const known = rowOf.get(id);
		if (known !== undefined) return known;
		const owners = ownersOf.get(id) ?? [];
		const value = owners.length === 0 ? 0 : Math.max(...owners.map(row)) + 1;
		rowOf.set(id, value);
		return value;
	};
	for (const id of slots.keys()) row(id);

	const rowCount = Math.max(0, ...rowOf.values()) + 1;
	let order: number[][] = Array.from({ length: rowCount }, () => []);
	for (const id of slots.keys()) order[rowOf.get(id) ?? 0]?.push(id);

	const adjacent = new Map<number, { id: number; weight: number }[]>();
	for (const link of links) {
		append(adjacent, link.owner, { id: link.owned, weight: link.weight });
		append(adjacent, link.owned, { id: link.owner, weight: link.weight });
	}
	const neighbors = (id: number, inRow: ReadonlySet<number>) =>
		(adjacent.get(id) ?? []).filter((neighbor) => inRow.has(neighbor.id));
	const sweep = (visit: (index: number, reference: number[]) => void) => {
		for (let pass = 0; pass < PASSES; pass += 1) {
			const downward = pass % 2 === 0;
			const indices = order.map((_, index) => index);
			for (const index of downward ? indices.slice(1) : indices.slice(0, -1).reverse()) {
				const reference = order[downward ? index - 1 : index + 1];
				if (reference !== undefined) visit(index, reference);
			}
		}
	};

	// Order each row by the mean position of its neighbors, which removes most crossings.
	sweep((index, reference) => {
		const position = new Map(reference.map((id, at) => [id, at]));
		const inRow = new Set(reference);
		order = order.map((ids, at) =>
			at !== index
				? ids
				: ids
						.map((id, original) => ({
							id,
							original,
							key: weightedMean(
								neighbors(id, inRow).map((n) => ({
									value: position.get(n.id) ?? 0,
									weight: n.weight,
								})),
							),
						}))
						.sort(
							(a, b) => (a.key ?? a.original) - (b.key ?? b.original) || a.original - b.original,
						)
						.map((item) => item.id),
		);
	});

	// Pull each slot's center toward its neighbors', keeping each row in order and unoverlapped.
	const width = (id: number) => slots.get(id)?.width ?? BOX_WIDTH;
	const gap = (left: number, right: number) => (width(left) + width(right)) / 2 + COLUMN_GAP;
	const center = new Map<number, number>();
	for (const ids of order) {
		ids.forEach((id, at) => {
			const previous = ids[at - 1];
			center.set(
				id,
				previous === undefined ? width(id) / 2 : (center.get(previous) ?? 0) + gap(previous, id),
			);
		});
	}
	sweep((index, reference) => {
		const ids = order[index] ?? [];
		const inRow = new Set(reference);
		const wished = ids.map(
			(id) =>
				weightedMean(
					neighbors(id, inRow).map((n) => ({ value: center.get(n.id) ?? 0, weight: n.weight })),
				) ??
				center.get(id) ??
				0,
		);
		// Heaviest-linked first, each at its wish within the room its placed neighbors leave, so a
		// parent sits over its subsidiaries and minority holders fill in around it.
		const weights = ids.map((id) =>
			neighbors(id, inRow).reduce((total, neighbor) => total + neighbor.weight, 0),
		);
		// Among equals, the middle of the run goes first, so owners of one entity fan out evenly
		// on both sides of it rather than all to one side.
		const atsByWeight = new Map<number, number[]>();
		weights.forEach((weight, at) => append(atsByWeight, weight, at));
		const middle = new Map([...atsByWeight].map(([weight, ats]) => [weight, mean(ats) ?? 0]));
		const byPriority = ids
			.map((id, at) => ({ id, at, weight: weights[at] ?? 0 }))
			.sort(
				(a, b) =>
					b.weight - a.weight ||
					Math.abs(a.at - (middle.get(a.weight) ?? 0)) -
						Math.abs(b.at - (middle.get(b.weight) ?? 0)) ||
					a.at - b.at,
			);
		// offsetOf[i]: the room the slots before i need, so the room between two is a difference.
		const offsetOf = [0];
		for (let at = 1; at < ids.length; at += 1) {
			const leftId = ids[at - 1];
			const rightId = ids[at];
			offsetOf.push(
				(offsetOf[at - 1] ?? 0) +
					(leftId === undefined || rightId === undefined ? 0 : gap(leftId, rightId)),
			);
		}
		const placed = new Map<number, number>();
		for (const { id, at } of byPriority) {
			let low = Number.NEGATIVE_INFINITY;
			let high = Number.POSITIVE_INFINITY;
			for (let other = 0; other < ids.length; other += 1) {
				const otherId = ids[other];
				const otherX = otherId === undefined ? undefined : placed.get(otherId);
				if (otherId === undefined || otherX === undefined) continue;
				const room = Math.abs((offsetOf[at] ?? 0) - (offsetOf[other] ?? 0));
				if (other < at) low = Math.max(low, otherX + room);
				else high = Math.min(high, otherX - room);
			}
			const want = wished[at] ?? 0;
			placed.set(id, low > high ? low : Math.min(Math.max(want, low), high));
		}
		ids.forEach((id) => center.set(id, placed.get(id) ?? 0));
	});

	const rowHeights = order.map((ids) =>
		Math.max(BOX_HEIGHT, ...ids.map((id) => slots.get(id)?.height ?? 0)),
	);
	const rowTops: number[] = [];
	rowHeights.reduce((top, height) => {
		rowTops.push(top);
		return top + height + ROW_GAP;
	}, MARGIN);
	const extents = [...center].map(([id, x]) => ({
		left: x - width(id) / 2,
		right: x + width(id) / 2,
	}));
	const left = Math.min(...extents.map((extent) => extent.left));
	const right = Math.max(...extents.map((extent) => extent.right));
	const toX = (slotCenter: number, slotWidth: number) => MARGIN + slotCenter - slotWidth / 2 - left;

	const byId = new Map(entities.map((entity) => [entity.id, entity]));
	const nodes: DiagramNode[] = [];
	const diagramStacks: DiagramStack[] = [];
	order.forEach((ids, index) => {
		const top = rowTops[index] ?? MARGIN;
		for (const id of ids) {
			const slotCenter = center.get(id) ?? 0;
			const entity = byId.get(id);
			if (entity !== undefined) {
				nodes.push({ entity, x: toX(slotCenter, BOX_WIDTH), y: top });
				continue;
			}
			const parentId = stackParentId.get(id);
			if (parentId === undefined) continue;
			const blockLeft = toX(slotCenter, width(id));
			const columns = stackColumns(stacks.get(parentId) ?? []).map((memberIds, column) => {
				const boxLeft = blockLeft + column * (BOX_WIDTH + TRUNK_INDENT + COLUMN_GAP) + TRUNK_INDENT;
				memberIds.forEach((memberId, at) => {
					const member = byId.get(memberId);
					if (member !== undefined)
						nodes.push({ entity: member, x: boxLeft, y: top + at * (BOX_HEIGHT + STACK_GAP) });
				});
				return { trunkX: boxLeft - TRUNK_INDENT / 2, memberIds };
			});
			diagramStacks.push({ parentId, busY: top - ROW_GAP / 2, columns });
		}
	});

	const last = rowCount - 1;
	return {
		nodes,
		edges: edges.filter((edge) => !stackOf.has(edge.ownedId)),
		stacks: diagramStacks,
		width: right - left + 2 * MARGIN,
		height: (rowTops[last] ?? MARGIN) + (rowHeights[last] ?? BOX_HEIGHT) + MARGIN,
	};
};

/** Splits a legal name into at most three lines of about `width` characters, as a box holds. */
export const wrapName = (name: string, width = 22, maxLines = 3): string[] => {
	const lines: string[] = [];
	for (const word of name.split(/\s+/).filter((part) => part !== "")) {
		const last = lines[lines.length - 1];
		if (last !== undefined && `${last} ${word}`.length <= width) {
			lines[lines.length - 1] = `${last} ${word}`;
		} else {
			lines.push(word);
		}
	}
	if (lines.length <= maxLines) return lines;
	const kept = lines.slice(0, maxLines);
	kept[maxLines - 1] = `${kept[maxLines - 1]?.slice(0, width - 1)}…`;
	return kept;
};
